/**
 * defineConfig — Wrapper around Playwright's defineConfig with sensible defaults.
 *
 * Automatically enables video capture, screenshots, and the TestRelic reporter
 * so users don't need to configure these manually.
 */

import { defineConfig as pwDefineConfig } from '@playwright/test';
import type { PlaywrightTestConfig, ReporterDescription } from '@playwright/test';
import type { ReporterConfig } from '@testrelic/core';

const REPORTER_MODULE = '@testrelic/playwright-analytics';

function hasTestRelicReporter(reporters: PlaywrightTestConfig['reporter']): boolean {
  if (!Array.isArray(reporters)) return false;
  return reporters.some(
    (r) => Array.isArray(r) && typeof r[0] === 'string' && r[0] === REPORTER_MODULE,
  );
}

/**
 * Wraps Playwright's `defineConfig` with TestRelic defaults:
 * - `use.video: 'on'`
 * - `use.screenshot: 'on'`
 * - `use.trace: 'on'` (captures network HAR, DOM snapshots, and console logs)
 * - Appends the TestRelic reporter if not already present
 *
 * User-provided values always take priority.
 *
 * @example
 * ```ts
 * import { defineConfig } from '@testrelic/playwright-analytics';
 *
 * export default defineConfig({
 *   testDir: './tests',
 *   // video, screenshot, and reporter are configured automatically
 * });
 * ```
 */
export function defineConfig(
  config: PlaywrightTestConfig,
  reporterOptions?: Partial<ReporterConfig>,
): ReturnType<typeof pwDefineConfig> {
  const userUse = config.use ?? {};

  const mergedUse = {
    video: 'on' as const,
    screenshot: 'on' as const,
    trace: 'on' as const,
    ...userUse,
  };

  // Preserve user-specified reporters. When no reporters are configured,
  // include Playwright's default stdout reporter so that built-in CLI
  // features like `--list` continue to work as expected.
  let existingReporters: ReporterDescription[];
  if (Array.isArray(config.reporter)) {
    existingReporters = config.reporter as ReporterDescription[];
  } else if (typeof config.reporter === 'string') {
    existingReporters = [[config.reporter]];
  } else {
    // No reporter configured — add Playwright's default list reporter
    // so stdout output (including --list) is preserved.
    existingReporters = [['list']];
  }

  const reporters: ReporterDescription[] = hasTestRelicReporter(existingReporters)
    ? existingReporters
    : [...existingReporters, [REPORTER_MODULE, reporterOptions ?? {}]];

  return pwDefineConfig({
    ...config,
    use: mergedUse,
    reporter: reporters,
  });
}
